import type { AppSchemasProgrammingSessionProgramPageSession } from "@classes-service-types/app-schemas-programming-session-program-page-session";
import type { EventOccurrence } from "@classes-service-types/event-occurrence";
import type { ProductPrice } from "@classes-service-types/product-price";
import type { ResponseBaseListResponseProductPrice } from "@classes-service-types/response-base-list-response-product-price";
import type { SessionEventsBase } from "@classes-service-types/session-events-base";
import type { SessionTravelDetail } from "@classes-service-types/session-travel-detail";
import { datadogLogs } from "@datadog/browser-logs";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import pLimit from "p-limit";
import { computed, type InjectionKey, provide, type Reactive, type Ref, reactive, readonly } from "vue";
import type { LocationQueryValue } from "vue-router";
import { findProductPriceBySession } from "@/composables/productPrices.js";
import eventsService from "@/services/events";
import productPriceService from "@/services/productPrices";
import { deriveExcursionRules, isExcursionIncluded as isExcursionIncludedByRules } from "@/util/excursionRules";
import type { ExcursionRules } from "../types/configurator";
import type { EventResponseBaseLocations, EventWithLocations, Excursion } from "../types/events";
import type { GetOccurrenceListResponse } from "../types/occurrences";
import { useUrlState } from "./useUrlState";

/** Return type of {@link useExcursions}. */
interface UseExcursionsReturn {
  excursionRules: Readonly<Ref<ExcursionRules>>;
  selectedExcursionIds: Ref<string[]>;
  excursionsStepIsComplete: () => boolean;
  isLoading: Ref<boolean>;
  errors: Ref<Error[]>;
  hasErrors: Ref<boolean>;
}

interface SelectedExcursions {
  eventInstanceIds: Readonly<Ref<readonly string[]>>;
  explicitlyRefuseExcursions: boolean;
}

/** Shape of the context provided to descendants for excursion selection. */
export interface ExcursionsProvide {
  /** Readonly view of selected excursion state (refs may be unwrapped by Vue's readonly). */
  excursions: Reactive<SelectedExcursions>;
  excursionRules: Readonly<Ref<ExcursionRules>>;
  isExcursionIncluded: (excursion: Excursion) => boolean;
  syncExcursionQueryParams: () => Promise<void>;
  addRegistration: (occurrenceId: string | LocationQueryValue) => void;
  removeRegistration: (occurrenceId: string | LocationQueryValue) => void;
  addMultipleRegistrations: (newOccurrenceIds: string[] | LocationQueryValue[]) => void;
  refuseExcursions: () => void;
  sessionExcursions: Ref<Excursion[] | undefined>;
  excursionsAreLoading: Ref<boolean>;
  selectedExcursionIds: Ref<string[]>;
}

export const ExcursionsKey: InjectionKey<ExcursionsProvide> = Symbol("excursions");

const toSingleId = (v: string | LocationQueryValue): string | null =>
  typeof v === "string" ? v : Array.isArray(v) ? ((v[0] as string) ?? null) : null;

const toIdList = (arr: string[] | LocationQueryValue[]): string[] =>
  arr.flatMap((item: string | LocationQueryValue): string[] =>
    typeof item === "string"
      ? [item]
      : Array.isArray(item)
        ? (item as string[]).filter((x): x is string => typeof x === "string")
        : [],
  );

const limit = pLimit(2);
const OCCURANCE_FETCH_LIMIT = 25;
const EXCURSIONS_QUERY_PARAM = "event_occurrences";

/**
 * Loads excursions for a program session and their pricing.
 * Uses the session's events and arrival date to resolve product prices and occurrence IDs,
 * then fetches full event details (with locations) and merges them into an Excursion list.
 *
 * @param session - Ref to the current program session, or undefined when no session is selected.
 * @returns {@link UseExcursionsReturn} with excursions ref, loading state, and errors.
 */
export const useExcursions = (
  session: Ref<AppSchemasProgrammingSessionProgramPageSession> | undefined,
): UseExcursionsReturn => {
  const { makeURLArrayState, updateURLState } = useUrlState();

  const selectedIds = makeURLArrayState(EXCURSIONS_QUERY_PARAM);

  const excursionRules = computed<ExcursionRules>(() =>
    deriveExcursionRules(session?.value?.session_event_selection_rules ?? []),
  );

  const isExcursionIncluded = (excursion: Excursion): boolean =>
    isExcursionIncludedByRules(excursionRules.value, excursion.id);

  const selectedExcursionIds = computed<string[]>(() => {
    const includedExcursions = excursions.value?.filter((e) => isExcursionIncluded(e)) ?? [];
    const includedIds: string[] = includedExcursions.map((e) => e.id);
    const optionalIds: string[] = selectedIds.value.map((id: string | LocationQueryValue) => id as string);
    return [...new Set([...includedIds, ...optionalIds])];
  });

  const selectedExcursions = reactive<SelectedExcursions>({
    eventInstanceIds: readonly(selectedExcursionIds),
    explicitlyRefuseExcursions: false,
  });

  /** Session travel details (arrival/departure) for the current session. */
  const sessionTravelDetails = computed<SessionTravelDetail[]>(() => {
    return session?.value?.session_travel_details ?? [];
  });

  /** Earliest arrival detail for the session, used to pick the correct product price. */
  const arrivalDetails = computed<SessionTravelDetail | undefined>(() => {
    const sortedAsc = sessionTravelDetails.value.sort((a, b) => {
      return Number(new Date(a.arrival_date ?? "")) - Number(new Date(b.arrival_date ?? ""));
    });
    return sortedAsc.length ? sortedAsc[0] : undefined;
  });

  /** Event IDs from the session's session_events (used to fetch product prices). */
  const eventProductIds = computed<string[]>(
    () => session?.value?.session_events?.map((event: SessionEventsBase) => event.event_id) ?? [],
  );

  /** Fetches product prices for the session's event product IDs. */
  const getEventProducts = async (): Promise<ProductPrice[]> => {
    const response: AxiosResponse<ResponseBaseListResponseProductPrice> = await productPriceService.getList({
      product_ids: eventProductIds.value,
      limit: OCCURANCE_FETCH_LIMIT,
    });
    return response?.data?.data?.items ?? [];
  };

  /** Product prices for the session's event product IDs. */
  const {
    data: productsData,
    isLoading: productsDataAreLoading,
    error: productsDataError,
  } = useQuery({
    queryKey: ["products", ...eventProductIds.value],
    enabled: () => eventProductIds.value.length > 0,
    queryFn: () => getEventProducts(),
  });

  /** Product prices for session events, keyed by product_id, resolved by arrival date. */
  const eventProducts = computed<Record<string, ProductPrice>>(() => {
    return eventProductIds.value
      .map((productId) => {
        const eventProducts = productsData.value?.filter((product) => product.product_id === productId) ?? [];
        const eventProduct: ProductPrice = findProductPriceBySession(
          eventProducts,
          arrivalDetails.value?.arrival_date,
        );
        return eventProduct ?? undefined;
      })
      .filter((e): e is ProductPrice => e != null)
      .reduce<Record<string, ProductPrice>>((acc, curr) => {
        acc[curr.product_id ?? ""] = curr;
        return acc;
      }, {});
  });

  /** Occurrence/product IDs that have a valid price for the session's arrival date. */
  const dateFilteredEventProductsIds = computed<string[]>(() => {
    return Object.keys(eventProducts.value);
  });

  /** Fetches occurrence list for the date-filtered product IDs (anonymous API). */
  const getSessionExcursions = async (): Promise<EventOccurrence[]> => {
    const response: AxiosResponse<GetOccurrenceListResponse> = await eventsService.getOccurrencesAnonymous({
      occurrence_ids: dateFilteredEventProductsIds.value,
      limit: OCCURANCE_FETCH_LIMIT,
    });
    return response?.data?.data?.items ?? [];
  };

  /**
   * Fetches full event details (with locations) for a single event. Does not throw; logs errors to Datadog
   * and returns undefined so it can be used safely inside Promise.all for batch loading.
   *
   * @param eventId - Event ID to load.
   * @returns Event with locations, or undefined on error or missing data.
   */
  const getExcursion = async (eventId: string): Promise<EventWithLocations | undefined> => {
    let response: EventWithLocations | undefined;
    try {
      const axiosResponse: AxiosResponse<EventResponseBaseLocations> = await eventsService.getOneAnonymous(eventId);
      response = axiosResponse?.data?.data;
    } catch (error) {
      // Log to Datadog
      if (error instanceof Error) {
        datadogLogs.logger.error(error.message, { eventId });
      } else {
        datadogLogs.logger.error("Error getting excursion", { eventId });
      }
    }
    return response;
  };

  /** Loads session excursions: occurrences → full event details → Excursion[] (occurrence + event + price). */
  const {
    data: excursions,
    isLoading: excursionsAreLoading,
    error: excursionsError,
  } = useQuery({
    queryKey: ["excursions", ...dateFilteredEventProductsIds.value],
    enabled: () => dateFilteredEventProductsIds.value.length > 0,
    queryFn: async (): Promise<Excursion[]> => {
      const sessionExcursions = await getSessionExcursions();
      const results = await Promise.all(
        sessionExcursions.map((excursion) => limit(() => getExcursion(excursion.event_id))),
      );
      const detailedSessionExcursions: EventWithLocations[] = results.filter(
        (e): e is EventWithLocations => e !== undefined,
      );
      const events: Record<string, EventWithLocations> = detailedSessionExcursions.reduce((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      }, {});
      return sessionExcursions.map((excursion) => {
        return {
          ...excursion,
          event: events[excursion.event_id],
          price: eventProducts.value[excursion.id].price_in_cents,
        } as unknown as Excursion;
      });
    },
  });

  const excursionsProvide: ExcursionsProvide = {
    excursions: selectedExcursions,
    excursionRules,
    isExcursionIncluded,
    syncExcursionQueryParams: () => updateURLState(EXCURSIONS_QUERY_PARAM, selectedExcursionIds.value),
    addRegistration: (occurrenceId: string | LocationQueryValue) => {
      const id = toSingleId(occurrenceId);
      if (id == null) return;
      selectedExcursions.explicitlyRefuseExcursions = false;
      if (!selectedIds.value.includes(id)) {
        selectedIds.value = selectedIds.value.concat([id]);
      }
    },
    removeRegistration: (occurrenceId: string | LocationQueryValue) => {
      const id = toSingleId(occurrenceId);
      if (id == null) return;
      selectedIds.value = selectedIds.value.filter((x) => x !== id);
    },
    addMultipleRegistrations: (newOccurrenceIds: string[] | LocationQueryValue[]) => {
      const ids = toIdList(newOccurrenceIds);
      selectedIds.value = [...selectedIds.value, ...ids.filter((id) => !selectedIds.value.includes(id))];
    },
    refuseExcursions: () => {
      selectedExcursions.explicitlyRefuseExcursions = true;
    },
    sessionExcursions: excursions,
    excursionsAreLoading,
    selectedExcursionIds,
  };
  provide(ExcursionsKey, excursionsProvide);

  const excursionsStepIsComplete = (): boolean => {
    // If the session has no excursions OR events have been added
    return (!isLoading.value && excursions.value?.length === 0) || selectedIds.value.length > 0;
  };

  /** True while either the products query or the excursions query is loading. */
  const isLoading = computed<boolean>(() => productsDataAreLoading.value || excursionsAreLoading.value);
  /** Non-null errors from the products and excursions queries. */
  const errors = computed<Error[]>(() => [productsDataError.value, excursionsError.value].filter(Boolean) as Error[]);
  /** True when at least one of the queries has errored. */
  const hasErrors = computed<boolean>(() => errors.value.length > 0);

  return {
    excursionRules,
    selectedExcursionIds,
    excursionsStepIsComplete,
    isLoading,
    errors,
    hasErrors,
  };
};
